import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationApi } from '../services/api';
import { useAuth } from './AuthContext';
import { AdminNotificationsSocket } from '../sockets/websocket_notifications';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const { token } = useAuth();
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const instanceCountRef = useRef(0);
  const connectionLockRef = useRef(false);
  const cleanupInProgressRef = useRef(false);

  const cleanup = useCallback(() => {
    if (cleanupInProgressRef.current) {
      console.log('[NotificationContext] Cleanup already in progress, skipping');
      return;
    }
    
    cleanupInProgressRef.current = true;
    console.log('[NotificationContext] Starting cleanup');
    
    try {
      if (reconnectTimeoutRef.current) {
        console.log('[NotificationContext] Clearing reconnect timeout');
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (socketRef.current) {
        console.log('[NotificationContext] Disconnecting existing socket');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      instanceCountRef.current = 0;
      connectionLockRef.current = false;
    } finally {
      cleanupInProgressRef.current = false;
    }
  }, []);

  const setupWebSocket = useCallback(() => {
    if (!token) {
      console.log('[NotificationContext] No token available, skipping WebSocket setup');
      return;
    }

    if (connectionLockRef.current) {
      console.log('[NotificationContext] Connection in progress, skipping duplicate setup');
      return;
    }

    if (socketRef.current?.isConnected) {
      console.log('[NotificationContext] WebSocket already connected, skipping setup');
      return;
    }

    connectionLockRef.current = true;
    
    try {
      cleanup();
      
      console.log('[NotificationContext] Creating new WebSocket instance');
      instanceCountRef.current++;
      
      const ws = new AdminNotificationsSocket(token);
      
      ws.on('unread_count', (count) => {
        setUnreadCount(count);
      });

      ws.on('connected', () => {
        console.log('[NotificationContext] WebSocket connected successfully');
        setIsConnected(true);
        fetchUnreadCount();
        connectionLockRef.current = false;
      });

      ws.on('disconnected', () => {
        console.log('[NotificationContext] WebSocket disconnected');
        setIsConnected(false);
        connectionLockRef.current = false;
        
        // Only attempt reconnect if this is still the current socket
        if (ws === socketRef.current && !cleanupInProgressRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[NotificationContext] Attempting reconnect after timeout');
            setupWebSocket();
          }, 5000);
        }
      });

      ws.on('error', (error) => {
        console.log('[NotificationContext] WebSocket error:', error);
        setIsConnected(false);
        connectionLockRef.current = false;
      });

      ws.connect();
      socketRef.current = ws;

    } catch (error) {
      console.error('[NotificationContext] Error setting up WebSocket:', error);
      setIsConnected(false);
      connectionLockRef.current = false;
    }
  }, [token, cleanup]);

  useEffect(() => {
    console.log('[NotificationContext] Setting up WebSocket connection');
    setupWebSocket();

    return () => {
      console.log('[NotificationContext] Component unmounting, cleaning up');
      cleanup();
    };
  }, [setupWebSocket, cleanup]);

  const fetchUnreadCount = async () => {
    if (!token) {
      return;
    }
    
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('[NotificationContext] Error fetching unread count:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setUnreadCount(0);
      return true;
    } catch (err) {
      return false;
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setUnreadCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (err) {
      return false;
    }
  };

  const value = {
    unreadCount,
    isConnected,
    markAllAsRead,
    markAsRead,
    fetchUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext; 